import {
  VCalendar,
  VCalendarCategory,
  VCalendarDaily,
  VCalendarWeekly,
  VCalendarMonthly
} from 'vuetify/lib';

import {
  DAY_MIN,
  DAYS_IN_WEEK,
  DAYS_IN_MONTH_MAX,
  relativeDays,
  nextDay,
  prevDay,
  copyTimestamp,
  updateWeekday,
  updateRelative,
  updateFormatted,
  getStartOfMonth,
  getEndOfMonth,
  timestampToDate,
  createNativeLocaleFormatter,
  getDayIdentifier
} from 'vuetify/lib/components/VCalendar/util/timestamp';

import props from './util/props';
import VCalendarWeeklyByEntity from './VCalendarWeeklyByEntity';

const WIDTH_FULL = 100;
const WIDTH_START = 95;

export default {
  extends: VCalendar,

  props: props.entity,

  computed: {
    renderProps() {
      const around = this.parsedValue;
      let component = null;
      let maxDays = this.maxDays;
      let weekdays = this.parsedWeekdays;
      let categories = this.parsedCategories;
      let start = around;
      let end = around;

      switch (this.type) {
        case 'month':
          component = VCalendarMonthly;
          start = getStartOfMonth(around);
          end = getEndOfMonth(around);
          break;

        case 'week':
          component = VCalendarDaily;
          start = this.getStartOfWeek(around);
          end = this.getEndOfWeek(around);
          maxDays = 7;
          break;

        case 'day':
          component = VCalendarDaily;
          maxDays = 1;
          weekdays = [start.weekday];
          break;

        case '4day':
          component = VCalendarDaily;
          end = relativeDays(copyTimestamp(end), nextDay, 4);
          updateFormatted(end);
          maxDays = 4;
          weekdays = [
            start.weekday,
            (start.weekday + 1) % 7,
            (start.weekday + 2) % 7,
            (start.weekday + 3) % 7
          ];
          break;

        case 'custom-weekly':
          component = VCalendarWeekly;
          start = this.parsedStart || around;
          end = this.parsedEnd;
          break;

        case 'custom-daily':
          component = VCalendarDaily;
          start = this.parsedStart || around;
          end = this.parsedEnd;
          break;

        case 'category':
          // eslint-disable-next-line no-case-declarations
          const days = this.parsedCategoryDays;
          component = VCalendarCategory;
          end = relativeDays(copyTimestamp(end), nextDay, days);
          updateFormatted(end);
          maxDays = days;
          weekdays = [];

          for (let i = 0; i < days; i++) {
            weekdays.push((start.weekday + i) % 7);
          }

          categories = this.getCategoryList(categories);
          break;

        case 'week-by-entity':
          component = VCalendarWeeklyByEntity;
          start = this.parsedStart || around;
          end = this.parsedEnd;
          break;

        default:
          throw new Error(this.type + ' is not a valid Calendar type');
      }

      return {
        component,
        start,
        end,
        maxDays,
        weekdays,
        categories
      };
    },

    monthLongFormatter() {
      return createNativeLocaleFormatter(this.currentLocale, () => ({
        timeZone: 'UTC',
        month: 'long'
      }));
    },

    monthShortFormatter() {
      return createNativeLocaleFormatter(this.currentLocale, () => ({
        timeZone: 'UTC',
        month: 'short'
      }));
    }
  },

  methods: {
    move(amount = 1) {
      const moved = copyTimestamp(this.parsedValue);
      const forward = amount > 0;
      const mover = forward ? nextDay : prevDay;
      const limit = forward ? DAYS_IN_MONTH_MAX : DAY_MIN;
      let times = forward ? amount : -amount;

      while (--times >= 0) {
        switch (this.type) {
          case 'month':
            moved.day = limit;
            mover(moved);
            break;

          case 'week':
            relativeDays(moved, mover, DAYS_IN_WEEK);
            break;

          case 'week-by-entity':
            relativeDays(moved, mover, DAYS_IN_WEEK * this.minWeeks);
            break;

          case 'day':
            relativeDays(moved, mover, 1);
            break;

          case '4day':
            relativeDays(moved, mover, 4);
            break;

          case 'category':
            relativeDays(moved, mover, this.parsedCategoryDays);
            break;
        }
      }

      updateWeekday(moved);
      updateFormatted(moved);
      updateRelative(moved, this.times.now);

      if (this.value instanceof Date) {
        this.$emit('input', timestampToDate(moved));
      } else if (typeof this.value === 'number') {
        this.$emit('input', timestampToDate(moved).getTime());
      } else {
        this.$emit('input', moved.date);
      }

      this.$emit('moved', moved);
    },
    getEventsForDayEntity(day) {
      const entity = day.entity;
      const events = this.getEventsForDay(day);

      return events.filter(
        ({ input }) => input[this.eventEntityId] === entity[this.entityId]
      );
    },

    updateEventVisibility() {
      if (this.noEvents || !this.eventMore) {
        return;
      }

      const updater = eventsMap => {
        const eventHeight = this.eventHeight;

        for (const date in eventsMap) {
          const { parent, events, more } = eventsMap[date];

          if (!more) {
            break;
          }

          const parentBounds = parent.getBoundingClientRect();
          const parentBoundsBottom = parentBounds.bottom - this.paddingDay;
          const last = events.length - 1;
          let hide = false;
          let hidden = 0;

          for (let i = 0; i <= last; i++) {
            if (!hide) {
              const eventBounds = events[i].getBoundingClientRect();
              hide =
                i === last
                  ? eventBounds.bottom > parentBoundsBottom
                  : eventBounds.bottom + eventHeight > parentBoundsBottom;
            }

            if (hide) {
              events[i].style.display = 'none';
              hidden++;
            }
          }

          if (hide) {
            more.style.display = '';
            more.innerHTML = this.$vuetify.lang.t(this.eventMoreText, hidden);
          } else {
            more.style.display = 'none';
          }
        }
      };

      if (this.type === 'week-by-entity') {
        const eventsMap = this.getEventsMapByEntity();
        for (const entityId in eventsMap) {
          updater(eventsMap[entityId]);
        }
      } else {
        updater(this.getEventsMap());
      }
    },

    getEventsMapByEntity() {
      const eventsMap = {};
      const elements = this.$refs.events;

      if (!elements || !elements.forEach) {
        return eventsMap;
      }

      elements.forEach(el => {
        const date = el.getAttribute('data-date');

        if (el.parentElement && date) {
          const entityId = el.parentElement.getAttribute('data-entity');

          if (!(entityId in eventsMap)) {
            eventsMap[entityId] = {};
          }

          if (!(date in eventsMap[entityId])) {
            eventsMap[entityId][date] = {
              parent: el.parentElement,
              more: null,
              events: []
            };
          }

          if (el.getAttribute('data-more')) {
            eventsMap[entityId][date].more = el;
          } else {
            eventsMap[entityId][date].events.push(el);
            el.style.display = '';
          }
        }
      });

      return eventsMap;
    },

    genDayEvent({ event }, day) {
      const eventHeight = this.eventHeight;
      const eventMarginBottom = this.eventMarginBottom;
      const dayIdentifier = getDayIdentifier(day);
      const week = day.week;
      const start = dayIdentifier === event.startIdentifier;
      let end = dayIdentifier === event.endIdentifier;
      let width = WIDTH_START;

      if (!this.categoryMode) {
        for (let i = day.index + 1; i < week.length; i++) {
          const weekdayIdentifier = getDayIdentifier(week[i]);

          if (event.endIdentifier >= weekdayIdentifier) {
            width += WIDTH_FULL;
            end = end || weekdayIdentifier === event.endIdentifier;
          } else {
            end = true;
            break;
          }
        }
      }

      const scope = {
        eventParsed: event,
        day,
        start,
        end,
        timed: false
      };
      return this.genEvent(event, scope, false, {
        staticClass: 'v-event',
        class: {
          'v-event-start': start,
          'v-event-end': end,
          'v-event-all-day': event.allDay
        },
        style: {
          height: `${eventHeight}px`,
          width: `${width}%`,
          'margin-bottom': `${eventMarginBottom}px`
        },
        attrs: {
          'data-date': day.date,
          draggable: 'true'
        },
        on: {
          dragstart: e => {
            e.dataTransfer.setData('application/json', JSON.stringify(event));
          }
        },
        key: event.index,
        ref: 'events',
        refInFor: true
      });
    }
  },

  render(h) {
    const {
      start,
      end,
      maxDays,
      component,
      weekdays,
      categories
    } = this.renderProps;

    const mode = this.eventModeFunction(
      this.parsedEvents,
      this.eventWeekdays[0],
      this.parsedEventOverlapThreshold
    );

    const slotDayEntity = this.$scopedSlots['day-entity'];

    return h(component, {
      staticClass: 'v-calendar',
      class: {
        'v-calendar-events': !this.noEvents
      },
      props: {
        ...this.$props,
        start: start.date,
        end: end.date,
        maxDays,
        weekdays,
        categories
      },
      directives: [
        {
          modifiers: {
            quiet: true
          },
          name: 'resize',
          value: this.updateEventVisibility
        }
      ],
      on: {
        ...this.$listeners,
        'click:date': day => {
          if (this.$listeners['input']) {
            this.$emit('input', day.date);
          }

          if (this.$listeners['click:date']) {
            this.$emit('click:date', day);
          }
        }
      },
      scopedSlots: {
        ...this.getScopedSlots(),
        'day-entity': day => {
          const events = this.getEventsForDayEntity(day);
          const visuals = mode(day, events, false, this.categoryMode);

          let children = [];
          visuals.forEach((visual, index) => {
            while (children.length < visual.column) {
              children.push(this.genPlaceholder(day));
            }

            const mapped = this.genDayEvent(visual, day);

            if (mapped) {
              children.push(mapped);
            }
          });

          if (children && children.length > 0 && this.eventMore) {
            children.push(this.genMore(day));
          }

          if (slotDayEntity) {
            const slot = slotDayEntity(day);

            if (slot) {
              children = children ? children.concat(slot) : slot;
            }
          }

          return children;
        }
      }
    });
  }
};
